/**
 * لقطة السلّة الحيّة للعميل المسجَّل — تُحفظ في public.cart_snapshots
 * ليتمكّن الأدمن من رؤية محتويات السلّة بالضبط في بطاقة العميل.
 */
import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

const itemSchema = z.object({
  handle: z.string().max(255).optional().nullable(),
  title: z.string().max(300).optional().nullable(),
  variantTitle: z.string().max(300).optional().nullable(),
  image: z.string().max(1000).optional().nullable(),
  price: z.number().min(0).optional().nullable(),
  quantity: z.number().int().min(0).max(999),
  isGiftSample: z.boolean().optional(),
  /** معرّف المتغيّر في Shopify — لازم لاستعادة السلّة على جهاز آخر. */
  variantId: z.string().max(255).optional().nullable(),
  selectedOptions: z
    .array(z.object({ name: z.string().max(120), value: z.string().max(200) }))
    .max(10)
    .optional(),
});


const schema = z.object({
  items: z.array(itemSchema).max(50),
  itemsCount: z.number().int().min(0).max(9999),
  cartValue: z.number().min(0),
  cartCurrency: z.string().max(5).optional().nullable(),
  market: z.string().max(10).optional().nullable(),
  locale: z.string().max(5).optional().nullable(),
  checkoutUrl: z.string().max(2000).optional().nullable(),
});

export const syncCartSnapshot = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) => schema.parse(data))
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;

    if (data.items.length === 0) {
      await supabase.from("cart_snapshots").delete().eq("user_id", userId);
      return { ok: true, cleared: true };
    }

    // ملاحظة: التفرّد على user_id فهرس جزئيّ (WHERE user_id IS NOT NULL)،
    // وPostgREST لا يستطيع استخدامه كـ arbiter في ON CONFLICT (خطأ 42P10).
    // لذلك: تحديث صريح، وإدراج فقط إن لم يوجد صفّ.
    const payload = {
      items: data.items,
      items_count: data.itemsCount,
      cart_value: data.cartValue,
      cart_currency: data.cartCurrency ?? null,
      market: data.market ?? null,
      locale: data.locale ?? null,
      checkout_url: data.checkoutUrl ?? null,
      updated_at: new Date().toISOString(),
    };

    const { data: updated, error: updateError } = await supabase
      .from("cart_snapshots")
      .update(payload)
      .eq("user_id", userId)
      .select("user_id");

    let error = updateError;
    if (!error && (updated?.length ?? 0) === 0) {
      const res = await supabase.from("cart_snapshots").insert({ user_id: userId, ...payload });
      error = res.error;
    }

    if (error) {
      console.error("[syncCartSnapshot]", error.message);
      return { ok: false as const };
    }
    return { ok: true, cleared: false };
  });

/** يُرجع لقطة سلّة المستخدم الحاليّ فقط — لاستعادة السلّة على جهاز آخر. */
export const getMyCartSnapshot = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabase, userId } = context;
    const { data, error } = await supabase
      .from("cart_snapshots")
      .select("items, items_count, cart_value, cart_currency, market, updated_at")
      .eq("user_id", userId)
      .maybeSingle();

    if (error) {
      console.error("[getMyCartSnapshot]", error.message);
      return { ok: false as const, snapshot: null };
    }
    if (!data) return { ok: true as const, snapshot: null };

    const parsedItems = z.array(itemSchema).safeParse(data.items ?? []);
    return {
      ok: true as const,
      snapshot: {
        items: parsedItems.success ? parsedItems.data : [],
        itemsCount: data.items_count ?? 0,
        cartValue: Number(data.cart_value ?? 0),
        cartCurrency: data.cart_currency ?? null,
        market: data.market ?? null,
        updatedAt: data.updated_at ?? null,
      },
    };
  });
